import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Icons,
  Modal,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { Post } from '@app/types/core';
import { createTag, fetchTags } from '@core/actions/tags/tag';
import { createTagPost, deleteTagPost } from '@core/actions/tags/tagPost';
import {
  accessingCurrentSpaceAsStaff,
  isInstructor,
} from '@core/selectors/classroom';
import { currentUserId } from '@core/selectors/user';
import { AulaDispatch, AulaState } from '@core/types/state';
import { createStyles } from './ResponsivePostTags.styles';

type Tag = { objectId?: string; name: string };

const TAG_LABEL_CHARACTER_LIMIT = 40;
const TAG_ROW_GAP = 8;
const MAX_TAG_ROWS = 2;
const tagLabelSx = {
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const getTagLabel = (name: string) =>
  name.length > TAG_LABEL_CHARACTER_LIMIT
    ? `${name.slice(0, TAG_LABEL_CHARACTER_LIMIT)}…`
    : name;

const TagChip = ({
  tag,
  isDesktop,
  ...chipProps
}: {
  tag: Tag;
  isDesktop: boolean;
  [key: string]: any;
}) => {
  const showTooltip = isDesktop && tag.name.length > TAG_LABEL_CHARACTER_LIMIT;

  return (
    <Tooltip
      title={tag.name}
      disableFocusListener={!showTooltip}
      disableHoverListener={!showTooltip}
      disableTouchListener={!showTooltip}
    >
      <Box
        component="span"
        sx={{ display: 'inline-flex', minWidth: 0, maxWidth: '100%' }}
      >
        <Chip
          label={
            <Box component="span" sx={tagLabelSx}>
              {getTagLabel(tag.name)}
            </Box>
          }
          {...chipProps}
        />
      </Box>
    </Tooltip>
  );
};

const compareTags = (a: Tag, b: Tag) => a.name.localeCompare(b.name);
const sameTag = (a: Tag, b: Tag) =>
  (a.objectId && a.objectId === b.objectId) ||
  a.name.toLocaleLowerCase() === b.name.toLocaleLowerCase();

const ResponsivePostTags = ({ post }: { post: Post }) => {
  const dispatch: AulaDispatch = useDispatch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleTagCount, setVisibleTagCount] = useState(0);
  const [tagListWidth, setTagListWidth] = useState(0);
  const [draftTags, setDraftTags] = useState<Tag[]>([]);
  const [query, setQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const tagListRef = useRef<HTMLDivElement>(null);
  const tagMeasureRef = useRef<HTMLDivElement>(null);
  const countMeasureRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const { tags, spaceTags, canManage } = useSelector((state: AulaState) => {
    const data = state.data as any;
    const storedPost = data.posts[post.objectId] || post;
    const resolvedTags = (storedPost.tagPosts || [])
      .map((tagPost: { tagId: string }) => data.tags[tagPost.tagId])
      .filter(Boolean)
      .map((tag: Tag) => ({ objectId: tag.objectId, name: tag.name }))
      .sort(compareTags);
    const isReadOnlyUser = accessingCurrentSpaceAsStaff(state);
    const isAuthor = currentUserId(state) === storedPost.user.objectId;

    return {
      tags: resolvedTags as Tag[],
      spaceTags: Object.values(data.tags || {}) as Tag[],
      canManage: !isReadOnlyUser && (isInstructor(state) || isAuthor),
    };
  });

  const openTagModal = () => {
    setDraftTags(tags);
    setQuery('');
    setIsOpen(true);
    dispatch(fetchTags(post.classRoom) as any).catch(() => undefined);
  };

  const suggestions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return [];
    return spaceTags
      .filter(
        (tag) =>
          !draftTags.some((selected) => sameTag(selected, tag)) &&
          tag.name.toLocaleLowerCase().includes(normalizedQuery)
      )
      .sort(compareTags)
      .slice(0, 8);
  }, [draftTags, query, spaceTags]);

  const addTag = (tag: Tag) => {
    if (!draftTags.some((selected) => sameTag(selected, tag))) {
      setDraftTags((current) => [...current, tag].sort(compareTags));
    }
    setQuery('');
  };

  const addTypedTag = () => {
    const name = query.trim();
    if (!name) return;
    const matchingTag = spaceTags.find(
      (tag) => tag.name.toLocaleLowerCase() === name.toLocaleLowerCase()
    );
    addTag(matchingTag || { name });
  };

  const close = () => {
    if (!isSaving) {
      setIsOpen(false);
      setQuery('');
    }
  };

  const apply = async () => {
    setIsSaving(true);
    try {
      const tagsToRemove = tags.filter(
        (tag) => !draftTags.some((draftTag) => sameTag(draftTag, tag))
      );
      const tagsToAdd = draftTags.filter(
        (tag) => !tags.some((existingTag) => sameTag(existingTag, tag))
      );
      await Promise.all(
        tagsToAdd.map(async (tag) => {
          const persistedTag = tag.objectId
            ? tag
            : await (dispatch(
                createTag(tag.name, post.classRoom) as any
              ) as any);
          await (dispatch(
            createTagPost(persistedTag.objectId, post.objectId) as any
          ) as any);
        })
      );
      await Promise.all(
        tagsToRemove
          .filter((tag) => tag.objectId)
          .map((tag) =>
            dispatch(
              deleteTagPost({
                tagId: tag.objectId!,
                postId: post.objectId,
              }) as any
            )
          )
      );
      setIsOpen(false);
      setQuery('');
    } finally {
      setIsSaving(false);
    }
  };

  useLayoutEffect(() => {
    const tagList = tagListRef.current;
    if (!tagList) return undefined;

    const updateWidth = () => setTagListWidth(tagList.clientWidth);
    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useLayoutEffect(() => {
    if (isExpanded) return;

    const availableWidth = tagListRef.current?.clientWidth;
    const tagWidths = Array.from(
      tagMeasureRef.current?.querySelectorAll<HTMLElement>(
        '[data-tag-measure]'
      ) || []
    ).map((element) => element.offsetWidth);

    if (!availableWidth || tagWidths.length !== tags.length) return;

    const itemsFitInTwoRows = (widths: number[]) => {
      let lineWidth = 0;
      let rows = 1;

      for (const width of widths) {
        const widthWithGap = lineWidth ? width + TAG_ROW_GAP : width;
        if (lineWidth && lineWidth + widthWithGap > availableWidth) {
          rows += 1;
          lineWidth = width;
        } else {
          lineWidth += widthWithGap;
        }
      }

      return rows <= MAX_TAG_ROWS;
    };

    let nextVisibleTagCount = 0;
    for (let count = tags.length; count >= 0; count -= 1) {
      const hiddenCount = tags.length - count;
      const countWidth = hiddenCount
        ? countMeasureRefs.current[hiddenCount]?.offsetWidth
        : 0;
      const controlWidths = [
        ...(hiddenCount && countWidth ? [countWidth] : []),
        ...(canManage ? [80] : []),
      ];

      if (itemsFitInTwoRows([...tagWidths.slice(0, count), ...controlWidths])) {
        nextVisibleTagCount = count;
        break;
      }
    }

    setVisibleTagCount((current) =>
      current === nextVisibleTagCount ? current : nextVisibleTagCount
    );
  }, [canManage, isDesktop, isExpanded, tagListWidth, tags.length]);

  const inlineTags = isExpanded ? tags : tags.slice(0, visibleTagCount);
  const hiddenCount = Math.max(tags.length - inlineTags.length, 0);
  const styles = createStyles(theme, tags.length > 0, isExpanded);
  const hasNewTag =
    query.trim() &&
    !spaceTags.some((tag) =>
      tag.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
    );

  if (!tags.length && !canManage) return null;

  return (
    <>
      <Box sx={styles.tagRow}>
        <Icons.TagOutlined aria-hidden="true" sx={styles.tagIcon} />
        <Box ref={tagListRef} sx={styles.tagList}>
          {inlineTags.map((tag) => (
            <TagChip
              key={tag.objectId}
              tag={tag}
              isDesktop={isDesktop}
              kind="default"
              size="small"
              sx={{ ...styles.tagChip, ...styles.staticTagChip }}
            />
          ))}
          {hiddenCount > 0 ? (
            <Chip
              label={`+ ${hiddenCount}`}
              kind="default"
              size="small"
              onClick={() => setIsExpanded(true)}
              sx={{ ...styles.tagChip, ...styles.interactiveTagChip }}
            />
          ) : null}
          {isExpanded && tags.length > visibleTagCount ? (
            <Chip
              label="Show less"
              kind="default"
              size="small"
              onClick={() => setIsExpanded(false)}
              sx={{ ...styles.tagChip, ...styles.showLessChip }}
            />
          ) : null}
          {canManage ? (
            <Button
              kind="secondary"
              size="small"
              onClick={openTagModal}
              aria-haspopup="dialog"
              sx={styles.addTagButton}
            >
              + Add Tag
            </Button>
          ) : null}
        </Box>
      </Box>

      <Box ref={tagMeasureRef} aria-hidden="true" sx={styles.tagMeasure}>
        {tags.map((tag) => (
          <TagChip
            key={tag.objectId}
            data-tag-measure
            tag={tag}
            isDesktop={isDesktop}
            kind="default"
            size="small"
            sx={{ ...styles.tagChip, ...styles.staticTagChip }}
          />
        ))}
        {tags.map((_, index) => {
          const count = index + 1;
          return (
            <Chip
              key={count}
              ref={(element) => {
                countMeasureRefs.current[count] = element;
              }}
              label={`+ ${count}`}
              kind="default"
              size="small"
              sx={styles.tagChip}
            />
          );
        })}
      </Box>

      <Modal
        open={isOpen}
        onClose={close}
        aria-labelledby="add-post-tags-title"
      >
        <Box role="dialog" aria-modal="true" sx={styles.modal}>
          <Box sx={styles.modalHeader}>
            <Typography
              id="add-post-tags-title"
              component="h2"
              sx={styles.modalTitle}
            >
              Add tags
            </Typography>
            <Tooltip
              title="Close"
              placement="top"
              disableFocusListener={!isDesktop}
              disableHoverListener={!isDesktop}
              disableTouchListener={!isDesktop}
            >
              <span>
                <IconButton
                  aria-label="Close add tags"
                  onClick={close}
                  disabled={isSaving}
                  sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                  <Icons.Close />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box sx={styles.searchContainer}>
            <TextField
              autoFocus
              aria-label="Search or add tags"
              aria-controls="tag-suggestions"
              aria-expanded={Boolean(query.trim())}
              label=""
              placeholder="Type here to add tags"
              value={query}
              sx={styles.searchField}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(event.target.value)
              }
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (suggestions[0]) addTag(suggestions[0]);
                  else addTypedTag();
                }
              }}
            />
            {query.trim() ? (
              <Box
                component="ul"
                id="tag-suggestions"
                role="listbox"
                aria-label="Tag suggestions"
                sx={styles.suggestionList}
              >
                {suggestions.map((tag, index) => (
                  <Box
                    component="li"
                    key={tag.objectId}
                    role="option"
                    tabIndex={0}
                    onClick={() => addTag(tag)}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        addTag(tag);
                      }
                    }}
                    sx={styles.suggestionItem(index === 0)}
                  >
                    <Typography component="span">{tag.name}</Typography>
                  </Box>
                ))}
                {hasNewTag ? (
                  <Box
                    component="li"
                    role="option"
                    tabIndex={0}
                    onClick={addTypedTag}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        addTypedTag();
                      }
                    }}
                    sx={styles.suggestionItem()}
                  >
                    <Typography component="span" sx={styles.newTagLabel}>
                      New tag:
                    </Typography>{' '}
                    <Typography component="span">{query.trim()}</Typography>
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
          {draftTags.length > 0 ? (
            <>
              <Typography component="h3" sx={styles.selectedTagsTitle}>
                Tags:
              </Typography>
              <Box sx={styles.selectedTags}>
                {draftTags.map((tag) => (
                  <TagChip
                    key={tag.objectId || tag.name}
                    tag={tag}
                    isDesktop={isDesktop}
                    kind="secondary"
                    size="small"
                    sx={styles.selectedTagChip}
                    onDelete={() =>
                      setDraftTags((current) =>
                        current.filter((item) => !sameTag(item, tag))
                      )
                    }
                  />
                ))}
              </Box>
            </>
          ) : null}
          <Box sx={styles.actions}>
            <Button kind="secondary" onClick={close} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              kind="primary"
              onClick={apply}
              disabled={isSaving}
              loading={isSaving}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ResponsivePostTags;
